import { Suspense } from "react";

import { PanelLoading } from "~/components/loading";
import type { CategoryRow } from "~/features/admin/categories/types";
import { CategoriesListView } from "./CategoriesListView";
import { CategoriesTabs } from "./CategoriesTabs";
import { CategoryHierarchyTab } from "./CategoryHierarchyTab";
import { CategoryLineageTab } from "./CategoryLineageTab";
import { CategoryPeek } from "./CategoryPeek";

type TabId =
  | "categories"
  | "entityCategoriesTree"
  | "relationCategoriesTree"
  | "entityCategoriesTable"
  | "relationCategoriesTable";

type TypeFilter = "entity" | "relation" | null;

interface CategoriesPresenterProps {
  activeTab: TabId;
  onActiveTabChange: (next: TabId) => void;

  rows: CategoryRow[];
  onSelect: (id: string) => void;

  search: string;
  onSearchChange: (next: string) => void;
  typeId: TypeFilter;
  onTypeIdChange: (next: TypeFilter) => void;
  excludeDeactivated: boolean;
  onExcludeDeactivatedChange: (next: boolean) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onRefresh: () => void;

  selectedCategory: string | null;
  onCloseSelected: () => void;
  onPeekPrev: (() => void) | null | undefined;
  onPeekNext: (() => void) | null | undefined;
}

export function CategoriesPresenter({
  activeTab,
  onActiveTabChange,
  rows,
  onSelect,
  search,
  onSearchChange,
  typeId,
  onTypeIdChange,
  excludeDeactivated,
  onExcludeDeactivatedChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onRefresh,
  selectedCategory,
  onCloseSelected,
  onPeekPrev,
  onPeekNext,
}: CategoriesPresenterProps) {
  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <CategoriesTabs active={activeTab} onChange={onActiveTabChange} />
      {activeTab === "categories" && (
        <CategoriesListView
          rows={rows}
          search={search}
          onSearchChange={onSearchChange}
          typeId={typeId}
          onTypeIdChange={onTypeIdChange}
          excludeDeactivated={excludeDeactivated}
          onExcludeDeactivatedChange={onExcludeDeactivatedChange}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          onRefresh={onRefresh}
          onSelect={onSelect}
        />
      )}
      {activeTab === "entityCategoriesTable" && (
        <Suspense fallback={<PanelLoading />}>
          <CategoryHierarchyTab categoryType="entity" onSelect={onSelect} />
        </Suspense>
      )}
      {activeTab === "relationCategoriesTable" && (
        <Suspense fallback={<PanelLoading />}>
          <CategoryHierarchyTab categoryType="relation" onSelect={onSelect} />
        </Suspense>
      )}
      {activeTab === "entityCategoriesTree" && (
        <Suspense fallback={<PanelLoading />}>
          <CategoryLineageTab categoryType="entity" onSelect={onSelect} selectedCategoryId={selectedCategory} />
        </Suspense>
      )}
      {activeTab === "relationCategoriesTree" && (
        <Suspense fallback={<PanelLoading />}>
          <CategoryLineageTab categoryType="relation" onSelect={onSelect} selectedCategoryId={selectedCategory} />
        </Suspense>
      )}
      <CategoryPeek id={selectedCategory} onClose={onCloseSelected} onPrevious={onPeekPrev} onNext={onPeekNext} />
    </div>
  );
}
